/** A single 360 icon on the 2D plan, in px (the 360 mini map scales from it, labels3dExt). */
export const PLAN_360_ICON_PX = 50;

/**
 * Shader definitions for 3D labels
 */
export const shaders = {
    vertexShader: `
        uniform int isCluster;
        uniform int isHighlighted;
        uniform int isLarge;
        uniform int isViewer2d;
        uniform int isTag;
        uniform float cameraX;
        uniform float cameraY;
        uniform float cameraZ;
        uniform float scaleFactor;
        uniform float visibilityRange;
        uniform float devicePixelRatio;
        uniform int is360Open;

        attribute vec3 position3d;

        varying vec3 vColor;
        varying vec3 vPos;

        void main() {
            float distance = sqrt(((cameraX - position.x)*(cameraX - position.x) + (cameraY - position.y)*(cameraY - position.y) + (cameraZ - position.z)*(cameraZ - position.z)));

            if(isViewer2d == 1){
                distance = sqrt(((cameraX - position3d.x)*(cameraX - position3d.x) + (cameraY - position3d.y)*(cameraY - position3d.y) + (cameraZ - position3d.z)*(cameraZ - position3d.z)));
            }

            if(visibilityRange < distance){
                gl_PointSize = 0.0;
                gl_Position = vec4(100.0, 100.0, 100.0, 0.0);
                return;
            }

            vColor = color;
            vPos = position;

            vec4 mvPosition = modelViewMatrix * vec4( position, 1.0 );

            if (isViewer2d == 0) {
                if(isTag == 0) {
                    if (is360Open == 0) {
                        // Normal 3D view — fixed size, no formula needed
                        gl_PointSize = 40.0;
                    } else {
                        // 360 viewer open — original distance-based formula
                        gl_PointSize = 20.0 * ( 20.0 / (length(mvPosition.xyz) + 1.0) ) * scaleFactor;
                        if(gl_PointSize < 20.0) gl_PointSize = 20.0;
                    }
                } else  {
                    // Tags — distance-based formula
                    gl_PointSize = 50.0 * ( 50.0 / (length(mvPosition.xyz) + 1.0) ) * scaleFactor;
                    if(gl_PointSize > 50.0) gl_PointSize = 50.0;
                }
            } else {
                if (isCluster == 1) {
                    gl_PointSize = 80.0;
                } else {
                    gl_PointSize = ${PLAN_360_ICON_PX.toFixed(1)};
                }
            }

            if (isHighlighted == 1) {
                gl_PointSize = gl_PointSize * 1.5;
            }

            if(isLarge == 1 && isViewer2d == 0) {
                gl_PointSize = gl_PointSize * 1.5;
            }

            gl_PointSize = gl_PointSize * devicePixelRatio;

            gl_Position = projectionMatrix * mvPosition;
        }
    `,

    fragmentShader: `
        uniform sampler2D tex;
        uniform sampler2D hoveringTexture;
        uniform int drawElevationRing;
        uniform vec3 elevationRingColor;
        uniform float opacity;
        varying vec3 vColor;
        varying vec3 vPos;
        void main() {
            vec2 uv = vec2(gl_PointCoord.x, 1.0 - gl_PointCoord.y);
            float distFromCenter = length(gl_PointCoord - vec2(0.5, 0.5));
            bool isHovering = (vColor.r > 4.0 && vColor.g > 4.0 && vColor.b > 4.0);

            if (isHovering) {
                gl_FragColor = texture2D(hoveringTexture, uv);

                // App primary color (#03624c)
                vec3 primaryColor = vec3(0.012, 0.384, 0.298);

                // Full-body tint (35% blend)
                gl_FragColor.rgb = mix(gl_FragColor.rgb, primaryColor, 0.35);

                // Thin outer ring at icon edge
                float hoverRing = smoothstep(0.41, 0.44, distFromCenter) * (1.0 - smoothstep(0.45, 0.49, distFromCenter));
                gl_FragColor.rgb = mix(gl_FragColor.rgb, primaryColor * 0.7, hoverRing);
                gl_FragColor.a = max(gl_FragColor.a, hoverRing);

                // Brightness boost to keep icon visible
                gl_FragColor.rgb *= 1.15;
            } else {
                gl_FragColor = texture2D(tex, uv);
            }

            // Elevation outline ring — thin colored stroke at the outermost edge
            // of the icon sprite, encoding the scan's elevation within its level.
            // Plateau 0.46-0.49 puts it right at the badge boundary so the ring
            // reads as "a colored outline around the icon", not "a band inside it".
            if (drawElevationRing == 1) {
                float elevRing = smoothstep(0.44, 0.46, distFromCenter) * (1.0 - smoothstep(0.49, 0.50, distFromCenter));
                gl_FragColor.rgb = mix(gl_FragColor.rgb, elevationRingColor, elevRing);
                gl_FragColor.a = max(gl_FragColor.a, elevRing);
            }

            if (gl_FragColor.w < 0.5) discard;
            // AVA-660: the 360 mini map fades scans by where they are from the open 360.
            gl_FragColor.a *= opacity;
        }
    `,



    tagsFragmentShader: `
        uniform sampler2D tex;
        uniform sampler2D hoveringTexture;
        uniform int useShaderIcon;
        uniform vec3 iconColor;
        uniform vec3 hoverColor;
        uniform float ringWidth;
        uniform float flickerOpacity;
        uniform int flickerState;
        varying vec3 vColor;
        varying vec3 vPos;

        void main() {
            vec2 center = vec2(0.5, 0.5);
            vec2 pos = gl_PointCoord;
            float distance = length(pos - center);

                        bool isHovering = (vColor.r > 4.0 && vColor.g > 4.0 && vColor.b > 4.0);
            bool isFlickering = (flickerState == 1);

            // Determine if we should show hover state (rings)
            // Show hover state if actually hovering OR if flickering to hover state
            bool showHoverState = isHovering || isFlickering;

            // Always use shader icons for tags now
            float alpha = 0.0;
            vec3 finalColor = iconColor;

            if (showHoverState) {
                // Hover state - outer ring extends to FULL size (0.5 = 100%)
                // New proportions: 50% center, 15% gap, 10% ring, 15% gap, 10% ring = 100% total
                // All elements fit perfectly within the full size

                float solidRadius = 0.25;      // 50% center circle
                float ring1Start = 0.345;      // After 15% gap (0.25 + 0.075)
                float ring1End = 0.375;        // 10% ring thickness (0.325 + 0.05)
                float ring2Start = 0.47;       // After 15% gap (0.375 + 0.075)
                float ring2End = 0.5;          // 10% outer ring extends to full size

                // Solid center circle (50% of total radius)
                float innerCircle = 1.0 - smoothstep(solidRadius - 0.01, solidRadius, distance);

                // First ring (5% thickness)
                float ring1 = smoothstep(ring1Start - 0.005, ring1Start, distance) *
                             (1.0 - smoothstep(ring1End, ring1End + 0.005, distance));

                // Second ring (5% thickness)
                float ring2 = smoothstep(ring2Start - 0.005, ring2Start, distance) *
                             (1.0 - smoothstep(ring2End, ring2End + 0.005, distance));

                // Combine all elements with same color
                alpha = max(innerCircle, max(ring1, ring2));
                finalColor = iconColor; // Use same color for all elements

            } else {
                // Normal state - simple filled circle (matches hover center size)
                float normalRadius = 0.25; // Same as hover solid center (50%)
                alpha = 1.0 - smoothstep(normalRadius - 0.01, normalRadius, distance);
                finalColor = iconColor;
            }

                                    // Apply 90% opacity for actual hover state highlight effect
            // For flicker state, use full opacity but alternate between normal and hover appearance
            float finalAlpha = alpha;
            if (isHovering && !isFlickering) {
                finalAlpha = alpha * 0.9; // Only apply 90% opacity for real hover, not flicker
            }

            // Apply flicker opacity for highlighting effect (this controls overall visibility)
            finalAlpha = finalAlpha * flickerOpacity;

            if (finalAlpha < 0.05) discard;
            gl_FragColor = vec4(finalColor, finalAlpha);
        }
    `,


};
